/**
 * MLS-DUAL-001 — shared MLS component controls.
 * WF-CONSOLIDATE (Aug 23, 2026): the compact ComponentActionBar under the
 * workspace tabs is GONE. Each component now has ONE authoritative action
 * area — Tax uses its existing Workflow Actions section (dual-aware Complete
 * wired in TaxCertificatePanel/JurisdictionManager), MLS uses the
 * MlsWorkflowActions section below (same TitleFlow visual pattern).
 * Exports:
 *   • AssignMenu          — TL/Admin assign/reassign dropdown (summary strip)
 *   • ReopenControl       — manager-only reopen beside a completed chip
 *   • MlsWorkflowActions  — authoritative MLS action area (Pending /
 *     Clarification / Mail-Away cards + Complete MLS Search) using existing
 *     component endpoints only.
 * MLS-ACCESS-CTRL (Aug 2026): AssignToMeButton removed — self-claim from
 * Fulfillment is disabled for all roles (backend endpoint returns 403).
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { notifyOrderCompletion } from './orderCompletionToast';
import { Loader2, RotateCcw, UserPlus, UserMinus, Mail, AlertTriangle, ChevronDown, Send, HelpCircle, CheckCircle, PauseCircle, PlayCircle, Clock } from 'lucide-react';
import { Button } from '../ui/button';
import { useAuth } from '../../App';
import { WorkflowActionsPanel, ReasonChipsField, ActiveStateCard, SubmitRow, CompletionChecklist } from '../workflow/WorkflowActionsPanel';
import { MlsMailAwaySendModal } from './MlsMailAwaySendModal';
import ReviewMlsReportModal from './ReviewMlsReportModal';
import { MAIL_AWAY_ACTIVE } from '../mailaway/shared/mailAwayStatus';
import { COMPONENT_LABELS, MLS_PENDING_REASONS, isMlsManagerRole, isMlsWorkerRole } from './rollupConfig';
import { formatDateTimeLocal, formatDateTimeShortET } from '../../utils/formatters';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtTs = (iso) => {
  if (!iso) return null;
  try {
    return formatDateTimeLocal(iso);
  } catch { return null; }
};

const componentCall = async (orderId, component, action, body, okMsg) => {
  try {
    const res = await axios.post(`${API}/api/orders/${orderId}/components/${component}/${action}`, body);
    window.dispatchEvent(new CustomEvent('tf:order-refresh')); // QC-CORRECTION-UI-001
    const oc = res.data?.order_completion; // MLS-AUTO-FINALIZE-001
    if (oc && notifyOrderCompletion(oc)) return true;
    if (res.data?.status === 'qc_review') { toast.success('MLS Search submitted for QC review — the Tax Search continues independently.', { duration: 7000 }); return true; }
    if (okMsg) toast.success(okMsg);
    return true;
  } catch (e) {
    const detail = e.response?.data?.detail;
    if (detail?.error === 'mls_search_incomplete' || detail?.error === 'tax_certificate_incomplete') {
      toast.error(
        <div>
          <strong>{detail?.error === 'tax_certificate_incomplete' ? 'Tax Certificate is incomplete:' : 'MLS Search entry is incomplete:'}</strong>
          <ul style={{ margin: '4px 0 0 0', paddingLeft: 16 }}>
            {(detail.validation_errors || []).map((er, i) => <li key={i} style={{ fontSize: 12 }}>{er}</li>)}
          </ul>
        </div>, { duration: 8000 });
    } else if (detail?.error === 'mail_away_outstanding') {
      toast.error(detail.message, { duration: 7000 });
    } else {
      toast.error(detail?.message || (typeof detail === 'string' ? detail : 'Action failed'));
    }
    return false;
  }
};

export const AssignMenu = ({ orderId, component, onChanged, currentAssignee }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState(null);
  const [busy, setBusy] = useState(false);
  // TL-SELF-ASSIGN-001: managers may self-assign a single component (same manager endpoint, target = me).
  const canSelfAssign = isMlsManagerRole(user?.role) && !!user?.id && currentAssignee !== user?.id;

  const loadUsers = async () => {
    if (users) return;
    try {
      const res = await axios.get(`${API}/api/users/processors`);
      // Components can only be assigned to Tax Team members.
      const list = (res.data || []).filter((u) => isMlsWorkerRole(u.role));
      list.sort((a, b) => (a.full_name || a.email || '').localeCompare(b.full_name || b.email || ''));
      setUsers(list);
    } catch { toast.error('Failed to load Tax Team members'); }
  };

  const assign = async (userId) => {
    setBusy(true);
    if (await componentCall(orderId, component, 'assign', { user_id: userId }, `${COMPONENT_LABELS[component]} assigned`)) {
      setOpen(false);
      onChanged?.();
    }
    setBusy(false);
  };

  const unassign = async () => {
    setBusy(true);
    if (await componentCall(orderId, component, 'unassign', undefined, `${COMPONENT_LABELS[component]} unassigned`)) {
      setOpen(false);
      onChanged?.();
    }
    setBusy(false);
  };

  return (
    <div className="relative inline-block">
      <Button
        type="button" variant="outline" size="sm"
        className="h-6 px-2 text-[11px]"
        data-testid={`mls-${component}-assign-btn`}
        onClick={() => { setOpen(!open); loadUsers(); }}
      >
        <UserPlus className="w-3 h-3 mr-1" />
        {currentAssignee ? 'Reassign' : 'Assign'}
        <ChevronDown className="w-3 h-3 ml-0.5" />
      </Button>
      {open && (
        <div className="absolute right-0 z-30 mt-1 w-52 max-h-56 overflow-auto rounded-md border border-gray-200 bg-white shadow-lg py-1"
          data-testid={`mls-${component}-assign-menu`}>
          {!users && <div className="px-3 py-2 text-xs text-gray-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>}
          {users && users.length === 0 && <div className="px-3 py-2 text-xs text-gray-400">No Tax Team members found</div>}
          {canSelfAssign && (
            <button
              type="button" disabled={busy}
              className="w-full text-left px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-50 border-b border-gray-100 flex items-center gap-1"
              data-testid={`mls-${component}-assign-to-me-menu-item`}
              onClick={() => assign(user.id)}
            >
              <UserPlus className="w-3 h-3" /> Assign to Me
            </button>
          )}
          {currentAssignee && (
            <button
              type="button" disabled={busy}
              className="w-full text-left px-3 py-1.5 text-xs text-rose-600 hover:bg-rose-50 disabled:opacity-50 border-b border-gray-100 flex items-center gap-1"
              data-testid={`mls-${component}-unassign-menu-item`}
              onClick={unassign}
            >
              <UserMinus className="w-3 h-3" /> Unassign
            </button>
          )}
          {(users || []).map((u) => (
            <button
              key={u.id} type="button" disabled={busy}
              className="w-full text-left px-3 py-1.5 text-xs hover:bg-indigo-50 disabled:opacity-50"
              data-testid={`mls-${component}-assign-user-${u.id}`}
              onClick={() => assign(u.id)}
            >
              {u.full_name || u.email}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const ReopenControl = ({ orderId, component, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  if (!open) {
    return (
      <Button type="button" variant="ghost" size="sm" className="h-6 px-1.5 text-[11px] text-gray-500"
        data-testid={`mls-${component}-reopen-btn`}
        onClick={() => setOpen(true)}>
        <RotateCcw className="w-3 h-3 mr-0.5" /> Reopen
      </Button>
    );
  }
  return (
    <span className="inline-flex items-center gap-1" data-testid={`mls-${component}-reopen-confirm-area`}>
      <input
        type="text" value={note} autoFocus
        onChange={(e) => setNote(e.target.value)}
        placeholder="Reopen reason"
        className="h-6 w-36 px-1.5 text-[11px] border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-400"
        data-testid={`mls-${component}-reopen-note-input`}
      />
      <Button type="button" size="sm" className="h-6 px-1.5 text-[11px]" disabled={busy}
        data-testid={`mls-${component}-reopen-confirm-btn`}
        onClick={async () => {
          setBusy(true);
          if (await componentCall(orderId, component, 'reopen', { enabled: true, note }, `${COMPONENT_LABELS[component]} reopened`)) {
            setOpen(false); setNote('');
            onChanged?.();
          }
          setBusy(false);
        }}>
        Confirm
      </Button>
      <button type="button" className="text-[11px] text-gray-400 hover:text-gray-600" onClick={() => setOpen(false)}>✕</button>
    </span>
  );
};

/**
 * MLS Workflow Actions — MLS-WF-UNIFY-001: the single authoritative action area
 * for the MLS component, now rendered through the shared WorkflowActionsPanel
 * (same StatusOptionCard cards as Tax). Pending | Clarification | Mail-Away each
 * expand an inline panel; active states are compact cards, never oversized
 * banners. Business logic is unchanged: component-scoped endpoints only
 * (/components/mls/pending|resume|clarification), Mail-Away stays inside
 * MlsMailAwayModal (chains, follow-up, receive, charging, guards).
 */
const MLS_COLORS = {
  amber: { bg: 'bg-amber-50', bgHover: 'hover:bg-amber-100', bgSelected: 'bg-amber-100', border: 'border-amber-200', borderSelected: 'border-amber-400', icon: 'text-amber-500', iconSelected: 'text-amber-600', ring: 'ring-amber-400' },
  purple: { bg: 'bg-purple-50', bgHover: 'hover:bg-purple-100', bgSelected: 'bg-purple-100', border: 'border-purple-200', borderSelected: 'border-purple-400', icon: 'text-purple-500', iconSelected: 'text-purple-600', ring: 'ring-purple-400' },
  blue: { bg: 'bg-blue-50', bgHover: 'hover:bg-blue-100', bgSelected: 'bg-blue-100', border: 'border-blue-200', borderSelected: 'border-blue-400', icon: 'text-blue-500', iconSelected: 'text-blue-600', ring: 'ring-blue-400' },
};
const MLS_ACTION_CARDS = [
  { value: 'pending', label: 'Pending', description: 'Waiting for department / response', Icon: Clock, colors: MLS_COLORS.amber },
  { value: 'clarification', label: 'Clarification', description: 'Need customer input', Icon: HelpCircle, colors: MLS_COLORS.purple },
  { value: 'mailaway', label: 'Mail-Away', description: 'Send / track municipal department request', Icon: Mail, colors: MLS_COLORS.blue },
];

export const MlsWorkflowActions = ({ orderId, cdoc, mailAwayRequests = [], readOnly, onChanged, parcelPresent = true, completionBlockers = [], mailAwaySectionsReady = null }) => {
  const { user } = useAuth();
  const isManager = isMlsManagerRole(user?.role);
  const isWorker = isMlsWorkerRole(user?.role);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [maSend, setMaSend] = useState(false); // MLS-MAILAWAY-ENTITY-001: card = action → entity picker
  const [review, setReview] = useState(false); // MLS-REVIEW-PACKAGE-001: mandatory report review before Complete
  // MLS-WORKFLOW-VALIDATION-001: server-computed gates (same rules the API enforces)
  const canComplete = completionBlockers.length === 0;
  // MLS-MAILAWAY-ENTITY-VALIDATION-001: Mail-Away needs a saved Parcel # AND at least one saved jurisdiction/entity
  const entityReady = !mailAwaySectionsReady || Object.values(mailAwaySectionsReady).some(Boolean);
  const mailAwayBlockMsg = !parcelPresent ? 'Parcel Number is required before initiating Mail-Away.'
    : (!entityReady ? 'Select a jurisdiction/entity before initiating Mail-Away.' : null);

  const status = cdoc?.status || 'pending';
  const mine = cdoc?.assignee_id && cdoc.assignee_id === user?.id;
  const canAct = !readOnly && isWorker && !!cdoc?.assignee_id && (mine || isManager);
  const returned = cdoc?.returned_at && status !== 'complete';

  const active = mailAwayRequests.filter((r) => MAIL_AWAY_ACTIVE.includes(r.status));

  // Read-only viewers with Mail-Away history may still open the modal to view it.
  if (!canAct && status !== 'complete' && mailAwayRequests.length === 0) return null;

  const act = async (action, body, okMsg) => {
    setBusy(true);
    if (await componentCall(orderId, 'mls', action, body, okMsg)) {
      setSelected(null); setReason(''); setNote('');
      onChanged?.();
    }
    setBusy(false);
  };
  const select = (v) => {
    if (v === 'mailaway') {
      if (mailAwayBlockMsg) { toast.error(mailAwayBlockMsg); return; }
      setMaSend(true); return;
    }
    setSelected(v); setReason(''); setNote('');
  };
  const noteOk = note.trim().length >= 3;

  const panels = {
    pending: (
      <>
        <ReasonChipsField category="mls_pending" reason={reason} onReasonChange={setReason} note={note} onNoteChange={setNote} accent="amber" Icon={Clock} testId="mls-mls-pending" />
        <SubmitRow label="Submit as Pending" testId="mls-mls-pending-submit-btn" className="bg-orange-500 hover:bg-orange-600 text-white"
          disabled={busy || !noteOk}
          onClick={() => act('pending', { reason: reason || 'Other', note }, 'MLS Search marked Pending')} />
      </>
    ),
    clarification: (
      <>
        <ReasonChipsField category="mls_clarification" reason={reason} onReasonChange={setReason} note={note} onNoteChange={setNote} accent="purple" Icon={HelpCircle} testId="mls-mls-clarification" />
        <SubmitRow label="Submit as Clarification" testId="mls-mls-note-submit-btn" className="bg-purple-600 hover:bg-purple-700 text-white"
          disabled={busy || !noteOk}
          onClick={() => act('clarification', { enabled: true, note, reason }, 'Moved to Clarification')} />
      </>
    ),
  };

  let activeState = null;
  if (status === 'qc_review') {
    // MLS-COMPONENT-QC-001: component under QC review — read-only for the processor, order & TAX continue
    activeState = (
      <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2.5 text-sm text-indigo-800" data-testid="mls-mls-qc-review-banner">
        <div className="font-semibold flex items-center gap-2"><Clock className="w-4 h-4" /> MLS Search is under QC review</div>
        <div className="text-[12px] mt-0.5">Submitted{cdoc?.qc_submitted_by_name ? ` by ${cdoc.qc_submitted_by_name}` : ''}{cdoc?.qc_submitted_at ? ` · ${fmtTs(cdoc.qc_submitted_at)}` : ''}. Edits are locked until QC approves or returns it.</div>
      </div>
    );
  } else if (status === 'complete') {
    activeState = (
      <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2.5 text-sm text-green-800 flex items-center gap-2" data-testid="mls-mls-completed-info">
        <CheckCircle className="w-4 h-4 flex-shrink-0" />
        MLS Search completed{cdoc?.completed_at ? ` ${fmtTs(cdoc.completed_at)}` : ''}{cdoc?.completed_by_name ? ` by ${cdoc.completed_by_name}` : ''}
      </div>
    );
  } else if (status === 'on_hold') {
    activeState = (
      <ActiveStateCard tone="amber" label="Pending" Icon={Clock} testId="mls-mls-onhold-banner"
        reason={<span data-testid="mls-mls-onhold-reason">{cdoc?.pending_reason || 'No reason recorded'}</span>}
        update={cdoc?.pending_note ? <span data-testid="mls-mls-onhold-note">Update: {cdoc.pending_note}</span> : null}
        meta={<span data-testid="mls-mls-onhold-meta">Marked by {cdoc?.pending_by_name || 'Unknown'}{cdoc?.pending_at ? ` · ${fmtTs(cdoc.pending_at)}` : ''}</span>}
        actions={canAct && (
          <Button type="button" size="sm" variant="outline" className="h-10 px-4 text-sm font-semibold rounded-xl border-orange-300 text-orange-700 hover:bg-orange-50 hover:text-orange-800 bg-white" disabled={busy}
            data-testid="mls-mls-resume-btn" onClick={() => act('resume', undefined, 'MLS Search resumed')}>
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5 mr-1" />} Resume
          </Button>
        )} />
    );
  } else if (status === 'clarification') {
    const received = cdoc?.clarification_received === true;
    activeState = (
      <ActiveStateCard tone="purple" label={received ? 'Clarification · Response Received' : 'Clarification'} Icon={HelpCircle} testId="mls-mls-clarification-banner"
        reason={cdoc?.clarification_reason ? <span data-testid="mls-mls-clarification-reason">{cdoc.clarification_reason}</span> : null}
        update={cdoc?.clarification_note ? <span data-testid="mls-mls-clarification-note">Update: {cdoc.clarification_note}</span> : null}
        meta={received ? <span data-testid="mls-mls-clarification-received-meta">Response received by {cdoc?.clarification_received_by_name || 'Unknown'}{cdoc?.clarification_received_at ? ` · ${formatDateTimeShortET(cdoc.clarification_received_at)}` : ''}</span> : null}
        actions={canAct && (
          <Button type="button" size="sm" className="h-8 px-3 text-xs bg-purple-600 hover:bg-purple-700 text-white" disabled={busy}
            data-testid="mls-mls-resolve-clarification-btn" onClick={() => act('clarification', { enabled: false }, received ? 'MLS processing resumed' : 'Clarification resolved')}>
            {received ? <PlayCircle className="w-3.5 h-3.5 mr-1" /> : <CheckCircle className="w-3.5 h-3.5 mr-1" />} {received ? 'Resume MLS Processing' : 'Resolve'}
          </Button>
        )} />
    );
  }
  // MLS-MAILAWAY-UI-CLEANUP-005: the expanded Mail-Away status panel + Manage button were removed from
  // Fulfillment (duplicate of Order Summary → Mail-Away Requests). The header chip stays as a compact signal.

  return (
    <>
      <WorkflowActionsPanel
        testId="mls-workflow-actions"
        cards={canAct ? MLS_ACTION_CARDS.map((c) => (c.value === 'mailaway' && mailAwayBlockMsg
          ? { ...c, disabled: true, disabledReason: mailAwayBlockMsg } : c)) : []}
        selected={selected}
        onSelect={select}
        panels={panels}
        activeState={activeState}
        headerRight={(
          <div className="flex items-center gap-2 text-[11.5px]">
            {active.length > 0 && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-purple-50 border border-purple-200 text-purple-700 text-[10px] font-semibold"
                data-testid="mls-mls-mailaway-chip" title={`${active.length} outstanding Mail-Away request(s) — must be received before this component can complete`}>
                <Mail className="w-3 h-3" /> Mail-Away ×{active.length}
              </span>
            )}
            {returned && (
              <span className="inline-flex items-center gap-1 text-rose-700" data-testid="mls-mls-returned-indicator">
                <AlertTriangle className="w-3 h-3" />
                Returned for correction{cdoc?.return_reason ? `: ${cdoc.return_reason}` : ''}
                {cdoc?.qc_status === 'returned' && !cdoc?.qc_feedback_acknowledged_at && canAct && (
                  <button type="button" className="ml-1 underline font-semibold" disabled={busy} data-testid="mls-mls-qc-acknowledge-btn"
                    onClick={() => act('qc-acknowledge', undefined, 'QC feedback acknowledged')}>Acknowledge QC feedback</button>
                )}
              </span>
            )}
          </div>
        )}
        footer={canAct && status !== 'complete' && status !== 'qc_review' && (
          <div className="space-y-2">
            {mailAwayBlockMsg && (
              <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800 flex items-start gap-2" data-testid="mls-mailaway-parcel-required">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <span>{mailAwayBlockMsg}</span>
              </div>
            )}
            {/* MLS-WF-PREMIUM-001: white checklist card (always visible) replaces the amber validation panel */}
            <CompletionChecklist blockers={completionBlockers} />
            <Button type="button" disabled={busy || !canComplete}
              className="w-full h-11 rounded-xl text-[15px] font-semibold bg-green-600 hover:bg-green-700 text-white shadow-sm disabled:bg-slate-300 disabled:text-slate-500 disabled:opacity-100 disabled:cursor-not-allowed disabled:shadow-none"
              data-testid="mls-mls-complete-btn" aria-disabled={!canComplete || undefined}
              title={canComplete ? undefined : 'Complete the outstanding requirements first'}
              onClick={() => setReview(true)}>
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Complete MLS Search
            </Button>
          </div>
        )}
      />
      <ReviewMlsReportModal open={review} onOpenChange={setReview} orderId={orderId}
        onConfirm={() => act('complete', undefined, 'MLS Search completed')} />
      {maSend && (
        <MlsMailAwaySendModal open orderId={orderId} mode="create" existingRequests={mailAwayRequests}
          onDone={() => { setMaSend(false); onChanged?.(); }} onClose={() => setMaSend(false)} />
      )}
    </>
  );
};
